"use client";

import { TypeReviews, TypeStarRatingSkeleton } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@yardzen/ui/components/button";
import { GoogleMark } from "@yardzen/ui/components/google-mark";
import { Link } from "@yardzen/ui/components/link";
import { REVIEWS_PAGE_HREF, YARDZEN_REVIEW_COUNT } from "./reviewSummary";
import { StarPill } from "./StarPill";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@yardzen/ui/components/carousel";
import { H3 } from "@yardzen/ui/components/h3";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { Entry } from "contentful";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useRef } from "react";
import Autoplay from "embla-carousel-autoplay";

const reviewVariant = cva("", {
  variants: {
    wrapperClass: {
      "side-navigation": "mb-4 flex flex-col gap-4 px-2 py-12 md:py-24",
      default: "mb-4 flex flex-col gap-4 px-8 py-12 md:py-24",
    },
    headerWrapperClass: {
      "side-navigation":
        "w-full flex-col items-center gap-2 text-center mx-auto",
      default: "w-full flex flex-col items-center gap-2 text-center mx-auto",
    },
    carouselClass: {
      "side-navigation": "px-12",
      default: "px-12",
    },
    carouselItemClass: {
      "side-navigation": "basis-full sm:basis-1/2 lg:basis-1/3",
      default: "basis-full md:basis-1/2 lg:basis-1/3",
    },
    cardClass: {
      "side-navigation":
        "text-black-elderberry relative flex h-full flex-col gap-4 rounded bg-white/50 p-6",
      default:
        "text-black-elderberry relative flex h-full flex-col gap-4 rounded bg-white/50 p-6",
    },
    cardTextClass: {
      "side-navigation": "text-base",
      default: "text-base",
    },
  },
});

export function Reviews({
  reviews,
}: {
  reviews: TypeReviews<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const router = useRouter();
  const reviewsArray = reviews.fields.reviews;
  const primaryCta = reviews.fields.CTAs?.find(
    (cta) => cta?.fields?.variant === "default",
  );
  const variant = reviews.fields.variant ?? "default";
  const variantResolved =
    variant === "side-navigation" ? "side-navigation" : "default";

  const autoplayPlugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true }),
  );

  const shouldShowDots = reviewsArray.length > 3;
  const shouldShowArrows = reviewsArray.length > 3;
  const shouldAutoScroll = variant === "auto-scrolling";

  return (
    <div className={reviewVariant({ wrapperClass: variantResolved })}>
      <div
        className={cn(
          "flex flex-col gap-6",
          variantResolved === "side-navigation" &&
            "md:flex-row md:items-start md:pl-8",
        )}
      >
        {/* Left section: Stars, Title, and CTA */}
        <div
          className={cn(
            "flex flex-col",
            variantResolved === "side-navigation"
              ? "gap-4 md:w-full md:max-w-[320px] md:flex-shrink-0 md:justify-start"
              : "justify-center",
          )}
        >
          <div
            className={cn(
              variantResolved === "side-navigation"
                ? "flex flex-col items-center gap-4 md:items-start"
                : reviewVariant({ headerWrapperClass: variantResolved }),
            )}
          >
            {/* Stars and Review Count */}
            <Link
              href={REVIEWS_PAGE_HREF}
              className={cn(
                "mb-2 flex",
                variantResolved === "side-navigation"
                  ? "justify-center md:justify-start"
                  : "justify-center",
              )}
            >
              <StarPill text={`(${YARDZEN_REVIEW_COUNT} Reviews)`} />
            </Link>

            {/* Title */}
            <H3
              className={cn(
                "font-display text-3xl md:text-5xl",
                variantResolved === "side-navigation"
                  ? "text-center md:text-left"
                  : "max-w-[850px]",
              )}
            >
              {reviews.fields.title}
            </H3>
          </div>

          {/* CTA Button - for side-navigation variant, show here on the left (desktop only) */}
          {primaryCta?.fields.url && variantResolved === "side-navigation" && (
            <Button
              variant="default"
              className="hidden w-fit rounded-full px-6 py-4 md:flex"
              onClick={() => {
                const url = primaryCta?.fields.url;
                if (url) {
                  router.push(url);
                }
              }}
              endIcon={<FontAwesomeIcon icon={faArrowRight} />}
            >
              {primaryCta?.fields.buttonText}
            </Button>
          )}
        </div>

        {/* Right section: Carousel */}
        <div
          className={cn(
            "flex flex-col py-4",
            variantResolved === "side-navigation" &&
              "md:min-w-0 md:flex-1 md:py-0",
          )}
        >
          <Carousel
            className={cn(
              variantResolved === "side-navigation"
                ? "px-12 md:px-10"
                : reviewVariant({
                    carouselClass: variantResolved,
                  }),
              reviewsArray.length <= 2 &&
                variantResolved !== "side-navigation" &&
                "px-4 md:px-12",
            )}
            opts={{
              align: reviewsArray.length <= 2 ? "start" : "start",
              loop:
                (variantResolved === "side-navigation" || shouldAutoScroll) &&
                reviewsArray.length > 3,
            }}
            plugins={shouldAutoScroll ? [autoplayPlugin.current] : undefined}
          >
            <CarouselContent
              className={cn(
                "-ml-4 pb-6",
                reviewsArray.length <= 2 && "md:justify-center",
              )}
              aria-live="off"
            >
              {reviewsArray.map((review) => {
                return (
                  <CarouselItem
                    key={review?.sys.id}
                    className={cn(
                      "pl-4",
                      reviewsArray.length <= 2
                        ? "basis-[calc(100%-1rem)] md:max-w-[600px] md:basis-1/2"
                        : reviewVariant({
                            carouselItemClass: variantResolved,
                          }),
                    )}
                  >
                    <Card
                      key={review?.sys.id}
                      name={review?.fields.name ?? ""}
                      quote={review?.fields.review ?? ""}
                      location={review?.fields.location ?? ""}
                      packageName={review?.fields.package ?? ""}
                      image={review?.fields.image}
                      variantResolved={variantResolved}
                    />
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            {/* Navigation Arrows - show when there are more than 3 reviews */}
            {shouldShowArrows && (
              <>
                <CarouselPrevious
                  className="text-black-elderberry left-0 h-8 w-8 border-0 bg-white shadow-lg hover:scale-110 hover:bg-white"
                  aria-label="Previous review"
                  variant="default"
                  icon={faChevronLeft}
                />
                <CarouselNext
                  className="text-black-elderberry right-0 h-8 w-8 border-0 bg-white shadow-lg hover:scale-110 hover:bg-white"
                  aria-label="Next review"
                  variant="default"
                  icon={faChevronRight}
                />
              </>
            )}

            {/* Bottom Dots - show when there are more than 3 reviews */}
            {shouldShowDots && (
              <CarouselDots
                dotClassName="h-2.5 w-2.5 bg-black-elderberry/30 border-0 rounded-full"
                selectedDotClassName="h-2.5 w-8 bg-black-elderberry border-0 rounded-full"
                count={reviewsArray.length}
                className="mt-6"
              />
            )}
          </Carousel>

          {/* CTA Button - centered below carousel */}
          {primaryCta?.fields.url && (
            <div
              className={cn(
                "mt-8 flex justify-center",
                variantResolved === "side-navigation" && "md:hidden",
              )}
            >
              <Button
                variant="default"
                className="w-fit rounded-full px-6 py-4"
                onClick={() => {
                  const url = primaryCta?.fields.url;
                  if (url) {
                    router.push(url);
                  }
                }}
                endIcon={<FontAwesomeIcon icon={faArrowRight} />}
              >
                {primaryCta?.fields.buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({
  name,
  quote,
  location,
  packageName,
  image,
  variantResolved,
}: {
  name: string;
  quote: string;
  location: string;
  packageName: string;
  image?: any;
  variantResolved: "default" | "side-navigation";
}) {
  const imageUrl = image?.fields?.file?.url;

  return (
    <div
      className={reviewVariant({
        cardClass: variantResolved,
      })}
    >
      {/* Name at top, with the Google mark the review was posted on */}
      <div className="flex items-start justify-between gap-3">
        <P className="font-display text-2xl">{name}</P>
        <GoogleMark className="mt-1 size-6" />
        <span className="sr-only">Review on Google</span>
      </div>

      {/* Quote - bigger text, flex-1 to push content to bottom */}
      <P
        noSpacing
        className={cn(
          "flex-1",
          reviewVariant({
            cardTextClass: variantResolved,
          }),
        )}
      >
        {quote}
      </P>

      {/* Package and Location - fixed at bottom */}
      <div className="flex flex-col gap-3">
        {(packageName || location) && (
          <P className="text-xs" noSpacing={true}>
            {packageName}
            {packageName && location && " · "}
            {location}
          </P>
        )}

        {/* Logo/Image at bottom */}
        {imageUrl && (
          <div>
            <Image
              src={`https:${imageUrl}`}
              width={54}
              height={33}
              alt={name}
              placeholder="empty"
              className="bg-transparent"
            />
          </div>
        )}
      </div>
    </div>
  );
}
